import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, Calendar, Clock, Trash2, Eye, Loader2, AlertTriangle, 
  Lock, Crown, ChevronDown, ChevronUp, Instagram, Twitter, 
  Linkedin, Facebook, Share2, RefreshCw, CheckCircle2, Filter, Cloud
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { calendarsApi } from '../services/calendarsApi';

const PLATFORM_ICONS = {
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  facebook: Facebook,
  tiktok: Share2,
  bluesky: Cloud,
};

const PLATFORM_COLORS = {
  instagram: 'text-pink-400',
  twitter: 'text-blue-400',
  linkedin: 'text-blue-600',
  facebook: 'text-blue-500',
  tiktok: 'text-slate-300',
  bluesky: 'text-sky-400',
};

// Progress bar component - moved outside to avoid recreation on every render
const ProgressBar = ({ stats }) => (
  <div className="flex items-center gap-2 mt-2">
    <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
      <div 
        className={`h-full transition-all duration-300 ${stats.posted > 0 ? 'bg-green-500' : 'bg-slate-600'}`}
        style={{ width: `${stats.percentage}%` }}
      />
    </div>
    <span className={`text-xs font-medium ${stats.posted > 0 ? 'text-green-400' : 'text-slate-500'}`}>
      {stats.posted}/{stats.total} posted
    </span>
  </div>
);

const CalendarHistory = ({ onClose, onLoadCalendar }) => {
  const { user } = useAuth();
  const [calendars, setCalendars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [expandedCalendar, setExpandedCalendar] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'has_posted'

  useEffect(() => {
    loadCalendars();
  }, [user]);

  const loadCalendars = async () => {
    if (!user?.uid) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await calendarsApi.getCalendars(user.uid, user.email, true, 50);
      setCalendars(data.calendars || []);
      setIsPremium(data.is_premium);
    } catch (err) {
      setError('Failed to load calendar history');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate posting stats for a calendar
  const getPostingStats = (calendar) => {
    const posts = calendar.posts || [];
    const total = posts.length;
    const posted = posts.filter(p => p.status === 'posted').length;
    const draft = total - posted;
    return { total, posted, draft, percentage: total > 0 ? Math.round((posted / total) * 100) : 0 };
  };

  // Filter calendars based on selection
  const filteredCalendars = useMemo(() => {
    if (filter === 'has_posted') {
      return calendars.filter(cal => {
        const stats = getPostingStats(cal);
        return stats.posted > 0;
      });
    }
    return calendars;
  }, [calendars, filter]);

  // Overall stats
  const overallStats = useMemo(() => {
    let totalPosts = 0;
    let totalPosted = 0;
    calendars.forEach(cal => {
      const stats = getPostingStats(cal);
      totalPosts += stats.total;
      totalPosted += stats.posted;
    });
    return { totalPosts, totalPosted, calendarsWithPosted: calendars.filter(c => getPostingStats(c).posted > 0).length };
  }, [calendars]);

  const handleDelete = async (calendarId) => {
    if (!window.confirm('Delete this calendar? This cannot be undone.')) return;
    
    try {
      await calendarsApi.deleteCalendar(calendarId, user.uid);
      setCalendars(prev => prev.filter(c => c.id !== calendarId));
    } catch (err) {
      alert('Failed to delete calendar');
    }
  };

  const handleLoadCalendar = (calendar) => {
    if (calendar.access_expired && !isPremium) {
      alert('Your access to this calendar has expired. Upgrade to a subscription or lifetime plan for extended history access.');
      return;
    }
    onLoadCalendar(calendar);
    onClose();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatExpirationDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const daysLeft = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { text: 'Expired', color: 'text-red-400', urgent: true };
    if (daysLeft === 0) return { text: 'Expires today', color: 'text-red-400', urgent: true };
    if (daysLeft === 1) return { text: 'Expires tomorrow', color: 'text-amber-400', urgent: true };
    if (daysLeft <= 3) return { text: `Expires in ${daysLeft} days`, color: 'text-amber-400', urgent: true };
    if (daysLeft <= 7) return { text: `Expires in ${daysLeft} days`, color: 'text-yellow-400', urgent: false };
    return { text: `Expires ${date.toLocaleDateString()}`, color: 'text-slate-500', urgent: false };
  };

  const getPlanBadge = (planType) => {
    switch (planType) {
      case 'super_admin':
        return <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded flex items-center gap-1"><Crown className="w-3 h-3" /> Admin</span>;
      case 'lifetime':
        return <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">Lifetime</span>;
      case 'subscription':
        return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Subscriber</span>;
      default:
        return <span className="px-2 py-0.5 bg-slate-500/20 text-slate-400 text-xs rounded">Pay-per-use</span>;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600/20 rounded-lg">
              <Calendar className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Calendar History</h2>
              <p className="text-sm text-slate-400">View and reload your generated calendars</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadCalendars}
              disabled={isLoading}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats Bar & Filter */}
        {!isLoading && calendars.length > 0 && (
          <div className="px-6 py-4 border-b border-slate-700 bg-slate-900/30">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              {/* Overall Stats */}
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400">{calendars.length} calendars</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span className="text-slate-400">
                    <span className="text-green-400 font-medium">{overallStats.totalPosted}</span> of {overallStats.totalPosts} posts published
                  </span>
                </div>
              </div>

              {/* Filter Toggle */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <div className="flex bg-slate-700 rounded-lg p-1">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      filter === 'all' 
                        ? 'bg-indigo-600 text-white' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    All ({calendars.length})
                  </button>
                  <button
                    onClick={() => setFilter('has_posted')}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      filter === 'has_posted' 
                        ? 'bg-green-600 text-white' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Has Posted ({overallStats.calendarsWithPosted})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-400">{error}</p>
              <button onClick={loadCalendars} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg">
                Retry
              </button>
            </div>
          ) : calendars.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Calendars Yet</h3>
              <p className="text-slate-400">Your generated calendars will appear here</p>
            </div>
          ) : filteredCalendars.length === 0 ? (
            <div className="text-center py-20">
              <CheckCircle2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Posted Content Yet</h3>
              <p className="text-slate-400">Mark posts as Posted when you publish them to see them here</p>
              <button 
                onClick={() => setFilter('all')}
                className="mt-4 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                View All Calendars
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Premium status banner */}
              {!isPremium && filter === 'all' && (
                <div className="bg-amber-900/20 border border-amber-800/50 rounded-lg p-4 flex items-center gap-3">
                  <Lock className="w-5 h-5 text-amber-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-amber-400 text-sm font-medium">Limited History Access</p>
                    <p className="text-amber-400/70 text-xs">Pay-per-use calendars expire after their duration. Upgrade for extended history.</p>
                  </div>
                </div>
              )}

              {filteredCalendars.map((calendar) => {
                const expiration = formatExpirationDate(calendar.access_expires_at);
                const isExpired = calendar.access_expired;
                const stats = getPostingStats(calendar);
                
                return (
                  <div 
                    key={calendar.id} 
                    className={`bg-slate-900/50 border rounded-xl overflow-hidden transition-all ${
                      isExpired ? 'border-red-800/50 opacity-60' : 'border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    {/* Calendar Header */}
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-bold text-white">{calendar.profile_name}</h3>
                            {getPlanBadge(calendar.user_plan_type)}
                            {isExpired && (
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded flex items-center gap-1">
                                <Lock className="w-3 h-3" /> Expired
                              </span>
                            )}
                            {stats.posted > 0 && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> {stats.posted} posted
                              </span>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {calendar.days}-day calendar
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatDate(calendar.created_at)}
                            </span>
                            <span>{calendar.posts?.length || 0} posts</span>
                          </div>

                          {/* Platforms */}
                          <div className="flex items-center gap-2 mt-3">
                            {calendar.platforms?.map(platform => {
                              const Icon = PLATFORM_ICONS[platform] || Share2;
                              return (
                                <span key={platform} className={`${PLATFORM_COLORS[platform] || 'text-slate-400'}`}>
                                  <Icon className="w-4 h-4" />
                                </span>
                              );
                            })}
                          </div>

                          {/* Progress Bar */}
                          <ProgressBar stats={stats} />

                          {/* Expiration warning */}
                          {expiration && !isExpired && expiration.urgent && (
                            <div className={`mt-3 text-xs ${expiration.color} flex items-center gap-1`}>
                              <AlertTriangle className="w-3 h-3" />
                              {expiration.text}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setExpandedCalendar(expandedCalendar === calendar.id ? null : calendar.id)}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
                            title="Preview posts"
                          >
                            {expandedCalendar === calendar.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleLoadCalendar(calendar)}
                            disabled={isExpired && !isPremium}
                            className={`p-2 rounded-lg ${
                              isExpired && !isPremium
                                ? 'text-slate-600 cursor-not-allowed'
                                : 'text-indigo-400 hover:text-indigo-300 hover:bg-indigo-900/30'
                            }`}
                            title={isExpired && !isPremium ? 'Access expired' : 'Load calendar'}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(calendar.id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Posts Preview */}
                    {expandedCalendar === calendar.id && (
                      <div className="border-t border-slate-700 p-4 bg-slate-800/50">
                        <h4 className="text-sm font-medium text-slate-400 mb-3">Posts Preview</h4>
                        <div className="grid gap-2 max-h-60 overflow-y-auto">
                          {calendar.posts?.slice(0, 10).map((post, idx) => (
                            <div key={idx} className={`bg-slate-900 rounded-lg p-3 text-sm border-l-2 ${
                              post.status === 'posted' ? 'border-green-500' : 'border-slate-600'
                            }`}>
                              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                                <span>Day {post.day || idx + 1}</span>
                                <div className="flex items-center gap-2">
                                  <span className="capitalize">{post.platform || calendar.platforms?.[0]}</span>
                                  {post.status === 'posted' ? (
                                    <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1">
                                      <CheckCircle2 className="w-3 h-3" /> Posted
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 bg-slate-600/50 text-slate-400 rounded text-xs">Draft</span>
                                  )}
                                </div>
                              </div>
                              <p className="text-slate-300 line-clamp-2">{post.content || post.text || 'No content'}</p>
                            </div>
                          ))}
                          {calendar.posts?.length > 10 && (
                            <p className="text-xs text-slate-500 text-center py-2">
                              +{calendar.posts.length - 10} more posts
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarHistory;
